import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { RefreshCw, AlertTriangle, Clock, Save, Search, Cpu, ExternalLink } from "lucide-react";
import { urgencyLevel, UrgencyBadge, ageLabel, hoursSince } from "./shared";
import {
  adminStats, adminRevenueSeries, adminWebhookDeliveries, adminAltTextAudit, adminTestWebhook,
  adminListQuotes, adminListOrders, adminAiBudgetStatus,
} from "../../lib/api";

export function DashboardTab({ pw }) {
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    adminStats(pw).then(setStats).catch(() => {});
    adminListQuotes(pw).then(setQuotes).catch(() => {});
    adminListOrders(pw).then(setOrders).catch(() => {});
  }, [pw]);

  const urgentQuotes = useMemo(
    () => quotes.filter((q) => q.status === "new" && urgencyLevel(q.created_at, true)),
    [quotes]
  );
  const urgentOrders = useMemo(
    () => orders.filter((o) => o.payment_status === "paid" && (o.fulfillment_status === "pending" || !o.fulfillment_status) && urgencyLevel(o.created_at, true)),
    [orders]
  );

  if (!stats) return <div className="text-zinc-500 font-mono">Loading…</div>;

  const tiles = [
    { l: "Revenue", v: `$${stats.total_revenue.toFixed(2)}`, accent: true },
    { l: "Paid Orders", v: stats.paid_orders },
    { l: "Pending", v: stats.pending_orders },
    { l: "Total Orders", v: stats.total_orders },
    { l: "Open Quotes", v: stats.open_quotes },
    { l: "Custom Products", v: stats.products_count },
    { l: "Ready-Made", v: stats.ready_made_count },
  ];

  return (
    <div className="space-y-6">
      {/* REPLY QUICKLY TRACKER */}
      {(urgentQuotes.length > 0 || urgentOrders.length > 0) ? (
        <div className="surface-card border-[var(--tc-red)] border-2 p-6" data-testid="reply-tracker">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle size={20} className="text-[var(--tc-red)]" />
            <h3 className="font-display text-2xl uppercase text-[var(--tc-red)]">Reply Quickly</h3>
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">{urgentQuotes.length + urgentOrders.length} ITEM{urgentQuotes.length + urgentOrders.length !== 1 ? "S" : ""} OVER 24H</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {urgentQuotes.length > 0 && (
              <div>
                <div className="font-mono text-[10px] tracking-widest text-zinc-400 mb-2">QUOTES · {urgentQuotes.length}</div>
                <ul className="space-y-2" data-testid="reply-tracker-quotes">
                  {urgentQuotes.slice(0, 5).map((q) => (
                    <li key={q.id} className="flex items-center justify-between bg-black border border-[#222] px-3 py-2">
                      <div className="min-w-0">
                        <div className="font-ui font-bold text-sm truncate">{q.name}</div>
                        <div className="font-mono text-[10px] text-zinc-500 truncate">{q.email} · {q.product_type || "—"} ×{q.quantity}</div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <UrgencyBadge createdAt={q.created_at} isOpen />
                        <div className="font-mono text-[10px] text-zinc-500 mt-1 flex items-center gap-1 justify-end"><Clock size={10} />{ageLabel(hoursSince(q.created_at))}</div>
                      </div>
                    </li>
                  ))}
                </ul>
                {urgentQuotes.length > 5 && <div className="font-mono text-[10px] text-zinc-500 mt-2">+ {urgentQuotes.length - 5} more</div>}
              </div>
            )}
            {urgentOrders.length > 0 && (
              <div>
                <div className="font-mono text-[10px] tracking-widest text-zinc-400 mb-2">ORDERS PENDING · {urgentOrders.length}</div>
                <ul className="space-y-2" data-testid="reply-tracker-orders">
                  {urgentOrders.slice(0, 5).map((o) => (
                    <li key={o.id} className="flex items-center justify-between bg-black border border-[#222] px-3 py-2">
                      <div className="min-w-0">
                        <div className="font-ui font-bold text-sm truncate">{o.order_number}</div>
                        <div className="font-mono text-[10px] text-zinc-500 truncate">{o.customer?.name} · ${o.total.toFixed(2)}</div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <UrgencyBadge createdAt={o.created_at} isOpen />
                        <div className="font-mono text-[10px] text-zinc-500 mt-1 flex items-center gap-1 justify-end"><Clock size={10} />{ageLabel(hoursSince(o.created_at))}</div>
                      </div>
                    </li>
                  ))}
                </ul>
                {urgentOrders.length > 5 && <div className="font-mono text-[10px] text-zinc-500 mt-2">+ {urgentOrders.length - 5} more</div>}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="surface-card border-green-700 border p-4 flex items-center gap-3" data-testid="reply-tracker-clear">
          <div className="w-2 h-2 bg-green-500 rounded-full" />
          <div className="font-mono text-[11px] tracking-widest text-green-400">ALL CLEAR — NO QUOTES OR ORDERS WAITING OVER 24H</div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
        {tiles.map((t) => (
          <div key={t.l} className="surface-card p-5">
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">{t.l.toUpperCase()}</div>
            <div className={`font-display text-4xl mt-1 ${t.accent ? "text-[var(--tc-red)]" : ""}`}>{t.v}</div>
          </div>
        ))}
      </div>

      <AiBudgetCard pw={pw} />

      {/* iter76c — WebhookHealthCard, WebhookDeliveriesCard, and
          AltTextAuditCard moved to Health · Verification tab so all
          health/verification widgets live in one place. Still exported
          from this file (see below) so HealthTab can import them. */}
      <RevenueChartCard pw={pw} />
    </div>
  );
}

export function AiBudgetCard({ pw }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    adminAiBudgetStatus(pw)
      .then(setStatus)
      .catch(() => setStatus({ error: true }))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    reload();
    const t = setInterval(reload, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pw]);

  if (loading && !status) {
    return (
      <div className="surface-card p-5" data-testid="ai-budget-card">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500">AI BUDGET</div>
        <div className="font-mono text-xs text-zinc-500 mt-2">Loading…</div>
      </div>
    );
  }
  if (!status || status.error) {
    return (
      <div className="surface-card p-5 border-yellow-700/50" data-testid="ai-budget-card-error">
        <div className="font-mono text-[10px] tracking-widest text-yellow-500">AI BUDGET · UNAVAILABLE</div>
        <div className="font-mono text-xs text-zinc-500 mt-2">Could not load — try refreshing.</div>
      </div>
    );
  }

  const exhausted = !!status.is_exhausted;
  const lastEvent = status.last_budget_event;
  const minutes = status.minutes_since_last_event;
  const minutesLabel =
    minutes == null ? null
    : minutes < 1 ? "just now"
    : minutes < 60 ? `${minutes}m ago`
    : minutes < 1440 ? `${Math.round(minutes / 60)}h ago`
    : `${Math.round(minutes / 1440)}d ago`;

  return (
    <div
      className={`surface-card p-5 ${exhausted ? "border-[var(--tc-red)]" : ""}`}
      data-testid="ai-budget-card"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Cpu size={14} className={exhausted ? "text-[var(--tc-red)]" : "text-zinc-400"} />
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">
              AI BUDGET · {exhausted ? "EXHAUSTED" : "HEALTHY"}
            </div>
            <span className={`chip text-[8px] ${exhausted ? "border-[var(--tc-red)] text-[var(--tc-red)]" : "border-green-700 text-green-400"}`}>
              {exhausted ? "TOPUP NEEDED" : "OK"}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 mt-3">
            <div>
              <div className="font-mono text-[9px] tracking-widest text-zinc-500">CALLS TODAY</div>
              <div className="font-display text-2xl">{status.today_calls ?? 0}</div>
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-widest text-zinc-500">CALLS / 30D</div>
              <div className="font-display text-2xl">{status.last_30d_calls ?? 0}</div>
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-widest text-zinc-500">PER-USER DAILY CAP</div>
              <div className="font-display text-2xl">{status.per_user_daily_cap ?? "—"}</div>
            </div>
          </div>
          {lastEvent && (
            <div className="font-mono text-[10px] text-zinc-500 mt-3">
              Last budget-exhausted event: <span className={exhausted ? "text-[var(--tc-red)]" : "text-zinc-400"}>{minutesLabel}</span>
              {lastEvent.source && <span className="text-zinc-600"> · {lastEvent.source}</span>}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <a
            href={status.topup_url || "https://app.emergent.sh/profile?tab=universal-key"}
            target="_blank"
            rel="noreferrer"
            data-testid="ai-budget-topup-link"
            className={`font-mono text-[10px] tracking-widest px-3 py-2 inline-flex items-center gap-2 border transition-colors ${
              exhausted
                ? "border-[var(--tc-red)] bg-[var(--tc-red)] text-white hover:bg-red-700"
                : "border-[#222] text-zinc-300 hover:border-white"
            }`}
          >
            ADD BALANCE <ExternalLink size={11} />
          </a>
          <button
            onClick={reload}
            data-testid="ai-budget-refresh"
            className="font-mono text-[10px] tracking-widest border border-[#222] hover:border-white px-3 py-2 inline-flex items-center gap-2 text-zinc-300"
          >
            <RefreshCw size={11} /> REFRESH
          </button>
        </div>
      </div>
      {exhausted && (
        <div className="mt-4 pt-3 border-t border-[#222] font-mono text-[11px] text-[var(--tc-red)]" data-testid="ai-budget-warning">
          ⚠ Customers can&rsquo;t generate AI designs right now. Top up at Profile &rarr; Universal Key &rarr; Add Balance.
        </div>
      )}
    </div>
  );
}

export function RevenueChartCard({ pw }) {
  const [series, setSeries] = useState([]);
  const [days, setDays] = useState(30);
  useEffect(() => {
    adminRevenueSeries(pw, days).then((r) => setSeries(r.series || [])).catch(() => setSeries([]));
  }, [pw, days]);
  const total = series.reduce((s, d) => s + (d.revenue || 0), 0);
  const orders = series.reduce((s, d) => s + (d.orders || 0), 0);
  const max = Math.max(1, ...series.map((d) => d.revenue || 0));
  return (
    <div className="surface-card p-5" data-testid="revenue-chart-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">/ REVENUE</div>
          <h3 className="font-display text-xl uppercase mt-1">Last {days} days</h3>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">PAID</div>
            <div className="font-display text-2xl text-[var(--tc-red)]">${total.toFixed(2)}</div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">{orders} order{orders !== 1 ? "s" : ""}</div>
          </div>
          <select
            value={days}
            onChange={(e) => setDays(+e.target.value)}
            data-testid="revenue-days-select"
            className="bg-black border border-[#222] px-2 py-1 text-xs font-mono"
          >
            <option value={7}>7d</option>
            <option value={30}>30d</option>
            <option value={90}>90d</option>
            <option value={180}>180d</option>
            <option value={365}>365d</option>
          </select>
        </div>
      </div>
      <div className="flex items-end gap-[3px] h-32" data-testid="revenue-spark">
        {series.map((d) => {
          const h = ((d.revenue || 0) / max) * 100;
          return (
            <div
              key={d.date}
              className="flex-1 bg-[var(--tc-red)] hover:bg-[var(--tc-red-deep)] transition-colors min-h-[2px]"
              style={{ height: `${Math.max(2, h)}%` }}
              title={`${d.date} · $${(d.revenue || 0).toFixed(2)} · ${d.orders} order(s)`}
            />
          );
        })}
      </div>
      <div className="flex justify-between font-mono text-[10px] tracking-widest text-zinc-500 mt-2">
        <span>{series[0]?.date || ""}</span>
        <span>{series[series.length - 1]?.date || ""}</span>
      </div>
    </div>
  );
}

export function WebhookDeliveriesCard({ pw }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setRows(await adminWebhookDeliveries(pw, 20)); } catch { /* ignore */ }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [pw]);
  return (
    <div className="surface-card p-5" data-testid="webhook-deliveries-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">/ WEBHOOK_DELIVERIES</div>
          <h3 className="font-display text-xl uppercase mt-1">Recent events</h3>
          <p className="font-body text-xs text-zinc-400 mt-1 max-w-xl">Last 20 Square webhook events delivered to this site (newest first).</p>
        </div>
        <button onClick={load} disabled={busy} data-testid="webhook-deliveries-refresh"
          className="btn-tc-secondary inline-flex items-center gap-2 text-xs disabled:opacity-50">
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> {busy ? "Loading…" : "Refresh"}
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-6 text-zinc-500 font-mono text-xs">No webhook deliveries logged yet.</div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.id} className="bg-black border border-[#222] px-3 py-2 flex items-center gap-3" data-testid={`webhook-delivery-${r.id}`}>
              <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${r.ok && !r.error ? "bg-green-500" : "bg-[var(--tc-red)]"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-ui text-sm truncate">
                  {r.event_type || "unknown"} {r.order_number && <span className="text-zinc-500">· {r.order_number}</span>}
                </div>
                <div className="font-mono text-[10px] text-zinc-500 truncate">
                  {new Date(r.received_at).toLocaleString()}{r.payment_status ? ` · payment_status=${r.payment_status}` : ""}{r.error ? ` · ERR: ${r.error}` : ""}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AltTextAuditCard({ pw }) {
  const [data, setData] = useState(null);
  useEffect(() => { adminAltTextAudit(pw).then(setData).catch(() => {}); }, [pw]);
  if (!data) return null;
  const totalMissing = (data.ready_made_missing_alt || []).length + (data.custom_missing_alt || []).length;
  return (
    <div className="surface-card p-5" data-testid="alt-audit-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">/ IMAGE_ALT_AUDIT</div>
          <h3 className="font-display text-xl uppercase mt-1">Alt text coverage</h3>
          <p className="font-body text-xs text-zinc-400 mt-1 max-w-xl">SEO bots use image alt text (derived from product names) for Image Search ranking.</p>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">MISSING</div>
          <div className={`font-display text-3xl ${totalMissing === 0 ? "text-green-400" : "text-[var(--tc-red)]"}`}>{totalMissing}</div>
          <div className="font-mono text-[10px] text-zinc-500">/ {data.ready_made_total + data.custom_total} TOTAL</div>
        </div>
      </div>
      {totalMissing > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {[...data.ready_made_missing_alt, ...data.custom_missing_alt].slice(0, 10).map((p, i) => (
            <li key={i} className="font-mono text-xs text-zinc-400">· {p.id}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function WebhookHealthCard({ pw }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const runTest = async () => {
    setBusy(true);
    try {
      const r = await adminTestWebhook(pw);
      setResult(r);
      if (r.local_route_reachable && r.signature_key_set) {
        toast.success("Webhook route + signature key both configured");
      } else if (!r.signature_key_set) {
        toast.error("SQUARE_WEBHOOK_SIGNATURE_KEY not set — add it in your env");
      } else if (!r.local_route_reachable) {
        toast.error("Local webhook route unreachable");
      } else {
        toast.message("Webhook check complete — see details");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Webhook test failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface-card p-5" data-testid="webhook-health-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">/ SQUARE_WEBHOOK</div>
          <h3 className="font-display text-xl uppercase mt-1">Webhook Health</h3>
          <p className="font-body text-xs text-zinc-400 mt-1 max-w-xl">
            Confirms Square is set up to push payment events to this site. Without it, the success page falls back to polling and can hang.
          </p>
        </div>
        <button
          onClick={runTest}
          disabled={busy}
          data-testid="test-webhook-btn"
          className="btn-tc-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
          {busy ? "Testing…" : "Test Webhook"}
        </button>
      </div>

      {result && (
        <div className="mt-4 grid md:grid-cols-2 gap-3 text-sm" data-testid="webhook-health-result">
          <Stat
            ok={result.local_route_reachable}
            label="Local Route"
            sub={`HTTP ${result.local_route_status ?? "—"} from this server`}
          />
          <Stat
            ok={result.signature_key_set}
            label="Signature Key"
            sub={result.signature_key_set
              ? "SQUARE_WEBHOOK_SIGNATURE_KEY is set"
              : "NOT set — webhook will accept unsigned bodies (sandbox-only)"}
          />

          <div className="md:col-span-2 mt-2 p-3 bg-black border border-[#222] font-mono text-xs">
            <div className="text-zinc-500 mb-2">EXPECTED WEBHOOK URL (paste this into Square)</div>
            <div className="text-white break-all flex items-center gap-2">
              <span data-testid="webhook-expected-url">{result.expected_url}</span>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(result.expected_url); toast.success("URL copied"); }}
                className="ml-auto text-[var(--tc-red)] border border-[var(--tc-red)] px-2 py-1 hover:bg-[var(--tc-red)] hover:text-white tracking-widest text-[10px]"
              >COPY</button>
            </div>
          </div>

          {!result.signature_key_set && (
            <div className="md:col-span-2 mt-3 p-4 border border-amber-700/60 bg-amber-950/30 text-sm" data-testid="webhook-setup-help">
              <div className="font-mono text-[10px] tracking-widest text-amber-300 mb-2">⚠ ACTION REQUIRED FOR PRODUCTION</div>
              <p className="text-zinc-300 mb-3">
                Square will push payment events to the URL above, but we can't verify they're really from Square until you add the signature key. To fix:
              </p>
              <ol className="list-decimal pl-5 space-y-2 text-zinc-300">
                <li>Open <a className="text-[var(--tc-red)] underline" href="https://developer.squareup.com/apps" target="_blank" rel="noreferrer">Square Developer Dashboard → your app → Webhooks</a></li>
                <li>Click <b>Add Subscription</b></li>
                <li>Paste the URL above into <b>Notification URL</b></li>
                <li>Under <b>Events</b>, select <code className="text-white">payment.created</code>, <code className="text-white">payment.updated</code>, <code className="text-white">refund.created</code>, <code className="text-white">refund.updated</code></li>
                <li>Choose API version (latest) and save</li>
                <li>Copy the <b>Signature Key</b> Square gives you</li>
                <li>Paste it into your production env as <code className="text-white">SQUARE_WEBHOOK_SIGNATURE_KEY=...</code></li>
                <li>Redeploy, then click <b>Test Webhook</b> again to confirm.</li>
              </ol>
            </div>
          )}

          {(result.errors || []).length > 0 && (
            <div className="md:col-span-2 mt-2 p-3 border border-[var(--tc-red)]/40 font-mono text-xs text-red-300">
              {result.errors.map((e, i) => <div key={i}>· {e}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Stat({ ok, label, sub }) {
  return (
    <div className={`p-3 border ${ok ? "border-green-700 bg-green-900/10" : "border-[var(--tc-red)] bg-[#1a0a0a]"}`}>
      <div className="font-mono text-[10px] tracking-widest text-zinc-400">{label.toUpperCase()}</div>
      <div className={`font-display text-xl mt-1 ${ok ? "text-green-400" : "text-[var(--tc-red)]"}`}>{ok ? "OK" : "FAIL"}</div>
      <div className="font-mono text-[10px] text-zinc-400 mt-1">{sub}</div>
    </div>
  );
}

export default DashboardTab;
